import { useState } from 'react';
import { useProject } from '@/hooks/useProject';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Check, Circle } from 'lucide-react';
import { ProjectKey } from '@/config/projects';

interface ProjectSelectorProps {
  className?: string;
}

export function ProjectSelector({ className }: ProjectSelectorProps) {
  const { currentProject, setCurrentProject, projectConfig, availableProjects } = useProject();
  const [isOpen, setIsOpen] = useState(false);

  const handleProjectChange = (projectKey: ProjectKey) => {
    setCurrentProject(projectKey);
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`justify-between min-w-[200px] ${className}`}
        >
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: projectConfig.color }}
            />
            <span className="font-medium">{projectConfig.name}</span>
            {projectConfig.hasData}
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Seleccionar Proyecto</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {availableProjects.map((project) => (
          <DropdownMenuItem
            key={project.key}
            onClick={() => handleProjectChange(project.key)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: project.color }}
              />
              <div className="flex flex-col">
                <span className="font-medium">{project.name}</span>
                <span className="text-xs text-muted-foreground">
                  {project.description}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {project.hasData && (
                <Badge variant="outline" className="text-xs">
                  <Circle className="w-2 h-2 mr-1" />
                  Datos
                </Badge>
              )}
              {currentProject === project.key && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-xs text-muted-foreground">
          Proyecto actual: {projectConfig.name}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ProjectSelector;
